import { useEffect, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, Check } from "@phosphor-icons/react";
import { CURRENCIES, CURRENCY_ORDER } from "../lib/pricingCurrency";
import useCurrency from "../lib/useCurrency";

/**
 * Currency selector inspired by myfxbook's flag pill in the nav.
 *
 * Compact button → opens a scrollable dropdown of 25 currencies with search.
 * Selection persists via useCurrency; every subscribed component re-renders.
 */
export default function CurrencyPicker({ variant = "nav" }) {
  const [ccy, setCcy] = useCurrency();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const active = CURRENCIES[ccy] || CURRENCIES.INR;
  const q = query.trim().toLowerCase();
  const items = CURRENCY_ORDER
    .map((code) => CURRENCIES[code])
    .filter((c) => !q
      || c.code.toLowerCase().includes(q)
      || c.name.toLowerCase().includes(q));

  const btnBase = variant === "nav"
    ? "flex items-center gap-1.5 h-9 px-2.5 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-xs font-semibold transition"
    : "flex items-center gap-1.5 h-9 px-2.5 rounded-full border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-900 text-xs font-semibold transition";

  return (
    <div ref={wrapRef} className="relative" data-testid="currency-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={btnBase}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="currency-picker-btn">
        <span className="text-base leading-none">{active.flag}</span>
        <span className="tracking-widest">{active.code}</span>
        <CaretDown size={11} weight="bold" className="opacity-70" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-2xl border border-white/10 bg-neutral-950/95 backdrop-blur-xl shadow-2xl z-50 overflow-hidden"
          style={{ boxShadow: "0 30px 80px -15px rgba(0,0,0,0.6)" }}
          role="listbox"
          data-testid="currency-picker-panel">
          <div className="p-2 border-b border-white/10">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/[0.04]">
              <MagnifyingGlass size={13} className="text-neutral-400" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search currency…"
                className="bg-transparent border-0 outline-none text-xs text-white placeholder:text-neutral-500 flex-1"
                data-testid="currency-picker-search" />
            </div>
          </div>
          <ul className="max-h-80 overflow-y-auto py-1" data-testid="currency-picker-list">
            {items.length === 0 && (
              <li className="px-3 py-4 text-xs text-neutral-500 text-center">No matches</li>
            )}
            {items.map((c) => {
              const selected = c.code === ccy;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => { setCcy(c.code); setOpen(false); setQuery(""); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-white/[0.06] transition text-xs ${selected ? "bg-white/[0.05]" : ""}`}
                    data-testid={`currency-option-${c.code}`}>
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold text-white">{c.code}</span>
                      <span className="text-neutral-500 ml-2 truncate">{c.name}</span>
                    </span>
                    <span className="text-neutral-500 text-[11px] font-mono">{c.symbol}</span>
                    {selected && <Check size={13} weight="bold" className="text-emerald-400" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-white/10 px-3 py-2 text-[10px] text-neutral-500 flex items-center justify-between">
            <span>Charged in INR at checkout</span>
            <span className="tracking-widest uppercase">≈ approx</span>
          </div>
        </div>
      )}
    </div>
  );
}
